/// <reference types="@types/google.maps" />

import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, Subscription, debounceTime } from 'rxjs';
import {
  AddressFieldValidationConfig,
  AddressFieldValidationRule,
  WorldAddressQuestion,
} from 'src/app/core/models/questionnaire.model';
import {
  EMPTY_PLACE_ADDITIONAL_INFO,
  GoogleApiError,
  GoogleApiErrorType,
  GooglePlacesService,
  PlaceAdditionalInfo,
} from 'src/app/core/services/google-places.service';
import { GeocodingService } from 'src/app/core/services/geocoding.service';
import { ViaCepService } from 'src/app/core/services/viacep.service';
import { MaterialModule } from '../../../modules/material/material-module.module';
import { QuestionAnswer } from '../../simple/question/question.component';
import { NgxMaskDirective } from 'ngx-mask';

// Re-export so any consumer that previously imported PlaceAdditionalInfo
// from this component file continues to work without changing its import.
export { PlaceAdditionalInfo } from 'src/app/core/services/google-places.service';

/**
 * Countries (Google Places `long_name`, English) whose postal addresses
 * conventionally include a county-level subdivision. Compared lowercase.
 *
 * Aliases ("usa", "uk") are included to tolerate manual entry where users
 * may type the short form instead of the full country name.
 */
// CPK-4150: countries whose name (as returned by Google Places / typed manually)
// identifies a Brazilian address, used to gate the `brazilExactDigits` postal
// code rule (postal formats vary by country, so this rule only applies here).
const BRAZIL_COUNTRY_NAMES: ReadonlySet<string> = new Set(['brazil', 'brasil', 'br']);

const COUNTRIES_USING_COUNTY: ReadonlySet<string> = new Set([
  'united kingdom',
  'uk',
  'great britain',
  'england',
  'scotland',
  'wales',
  'northern ireland',
  'united states',
  'united states of america',
  'usa',
  'ireland',
  'republic of ireland',
]);

@Component({
  selector: 'app-question-world-address',
  templateUrl: './question-world-address.component.html',
  styleUrls: ['./question-world-address.component.scss'],
  standalone: true,
  imports: [CommonModule, MaterialModule, ReactiveFormsModule, FormsModule, TranslateModule, NgxMaskDirective],
})
export class QuestionWorldAddressComponent implements OnInit, OnDestroy {
  @Input('question') question!: WorldAddressQuestion;
  @Output('changeAnswer') changeAnswer: EventEmitter<QuestionAnswer> =
    new EventEmitter();

  // -------------------------------------------------------------------------
  // Search state
  // -------------------------------------------------------------------------

  addressList: google.maps.places.AutocompletePrediction[] = [];
  addressForm = new FormGroup({
    address: new FormControl<string>(''),
    addtional_information: new FormControl(),
  });

  loading = false;

  /** Non-null when the last Google API call failed. */
  apiError: GoogleApiErrorType | null = null;

  // -------------------------------------------------------------------------
  // Mode
  // -------------------------------------------------------------------------

  /**
   * `'online'`  – showing Google Autocomplete search box.
   * `'offline'` – showing manual address fields.
   */
  mode: 'online' | 'offline' = 'online';

  // -------------------------------------------------------------------------
  // Address detail fields
  // -------------------------------------------------------------------------

  showAdd = false;
  additional_info: PlaceAdditionalInfo = { ...EMPTY_PLACE_ADDITIONAL_INFO };

  requiredFields: Record<string, string> = {
    streetName: 'Street name',
    streetNumber: 'Street number',
    city: 'City',
    state: 'State',
    country: 'Country',
    postalCode: 'Postal code',
  };

  fieldValidations: Record<string, boolean> = {
    streetName: false,
    streetNumber: false,
    city: false,
    state: false,
    country: false,
    postalCode: false,
    neighborhood: true,
    county: true,
    buildingName: true,
    unitNumber: true,
    referencePoint: true,
  };

  /**
   * CPK-4150: translation key for the format-specific error message per field
   * (min length, invalid characters, alphanumeric, CEP digit count). `null`
   * means the field is simply empty — the existing "is required" text applies.
   */
  fieldErrorKeys: Record<string, string | null> = {
    streetName: null,
    streetNumber: null,
    city: null,
    state: null,
    country: null,
    postalCode: null,
    neighborhood: null,
    county: null,
    buildingName: null,
    unitNumber: null,
    referencePoint: null,
  };

  /**
   * CPK-4150: optional fields — no presence requirement, but still subject to
   * the GLOBAL character deny-pattern ("restringir uso de caracteres em todos
   * os campos") when filled in.
   */
  private readonly optionalValidatedFields = [
    'neighborhood',
    'county',
    'buildingName',
    'unitNumber',
    'referencePoint',
  ];
  currentMask: string | null = null;

  // -------------------------------------------------------------------------
  // CEP / offline state
  // -------------------------------------------------------------------------

  cepLoading = false;

  // -------------------------------------------------------------------------
  // Private
  // -------------------------------------------------------------------------

  private readonly callAPI = new Subject<void>();
  private readonly cepSubject = new Subject<string>();
  private readonly subscriptions: Subscription[] = [];

  constructor(
    public zone: NgZone,
    private googlePlacesService: GooglePlacesService,
    private viaCepService: ViaCepService,
    private geocodingService: GeocodingService,
  ) {}

  /**
   * Whether the "County" field should be rendered.
   *
   * Visibility is driven by the selected country: only addresses whose
   * country conventionally uses a county-level subdivision (UK, US, Ireland)
   * show the field. While no country is set yet the field stays hidden.
   *
   * The `configuration.hideCounty` flag remains as a per-question manual
   * override that always wins.
   */
  get showCounty(): boolean {
    if (this.question.configuration?.hideCounty) return false;

    const country = (this.additional_info.country ?? '').trim().toLowerCase();
    if (!country) return false;

    return COUNTRIES_USING_COUNTY.has(country);
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  async ngOnInit(): Promise<void> {
    // Expose component instance to browser console for live inspection
    (window as any).__qwa = {
      component: this,
      search: (q: string) => {
        this.addressForm.controls.address.setValue(q);
        this.doSearch();
        console.log('[QWA] 🔍 Manual search triggered:', q);
      },
      state: () => ({
        mode: this.mode,
        apiError: this.apiError,
        showAdd: this.showAdd,
        loading: this.loading,
        cepLoading: this.cepLoading,
        additional_info: this.additional_info,
        addressList: this.addressList,
      }),
    };
    console.log('[QWA] 🪝 Hook disponível: window.__qwa  |  .search(q)  .state()');

    // Restore previously saved answer
    if (this.question.answer?.value) {
      this.addressForm.controls.address.setValue(
        this.question.answer.value.toString(),
      );
      this.addressForm.controls.addtional_information.setValue(
        this.question.answer.addtional_information,
      );

      if (this.question.answer.addtional_information) {
        const saved = this.question.answer.addtional_information;
        const resolvedVia = saved.resolvedVia ?? 'google_places';

        this.additional_info = {
          streetNumber: saved.streetNumber || '',
          streetName: saved.streetName || '',
          city: saved.city || '',
          neighborhood: saved.neighborhood || '',
          state: saved.state || '',
          county: saved.county || '',
          country: saved.country || '',
          postalCode: saved.postalCode || '',
          buildingName: saved.buildingName || '',
          unitNumber: saved.unitNumber || '',
          referencePoint: saved.referencePoint || '',
          latitude: saved.latitude ?? null,
          longitude: saved.longitude ?? null,
          resolvedVia,
        };
        this.showAdd = true;
        this.validateRequiredFields();

        console.log('[QWA] 💾 Estado restaurado', {
          resolvedVia,
          latitude: this.additional_info.latitude,
          longitude: this.additional_info.longitude,
          city: this.additional_info.city,
          country: this.additional_info.country,
        });

        // Restore offline mode for non-Google answers
        if (resolvedVia !== 'google_places') {
          this.mode = 'offline';
          console.log('[QWA] 🔀 Modo restaurado: offline (resolvedVia=%s)', resolvedVia);
        }
      }
    }

    if (!this.question.elective) {
      this.addressForm.controls.address.setValidators(Validators.required);
      this.addressForm.controls.addtional_information.setValidators(
        Validators.required,
      );
      this.addressForm.updateValueAndValidity();
      await new Promise<void>((resolve) => setTimeout(resolve, 100));
    }

    this.change();

    // Debounced search (online mode)
    this.subscriptions.push(
      this.callAPI.pipe(debounceTime(1000)).subscribe(() => {
        const value = this.addressForm.controls.address.value;
        console.log('[QWA] ⏱️  Triggering search for:', value);
        if (value) {
          this.addressForm.controls.addtional_information.reset();
          this.performSearch(value.trim());
        }
      }),
    );

    // Debounced CEP lookup (offline mode)
    this.subscriptions.push(
      this.cepSubject.pipe(debounceTime(800)).subscribe((cep) => {
        this.performCepLookup(cep);
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.callAPI.complete();
    this.cepSubject.complete();
  }

  // -------------------------------------------------------------------------
  // Online mode — search
  // -------------------------------------------------------------------------

  doSearch(input?: HTMLInputElement): void {
    if (this.mode !== 'online') return;
    this.loading = true;
    this.apiError = null;

    const rawValue = input?.value || '';

    const clean = rawValue.replace(/\D/g, '');

    const shouldUseCepMask =
      clean.length === 8 &&
      /^[0-9]+$/.test(clean);

    const newMask = shouldUseCepMask
      ? '00000-000'
      : null;

    const maskChanged = this.currentMask !== newMask;

    if (maskChanged) {
      this.currentMask = newMask;

      // espera Angular + ngx-mask aplicar
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {

          const maskedValue = input?.value ?? '';

          // sincroniza form control
          this.addressForm.controls.address.setValue(
            maskedValue,
            { emitEvent: false }
          );

          this.callAPI.next();
        });
      });

      return;
    }

    this.callAPI.next();
    
  }

  private async performSearch(searchValue: string): Promise<void> {
    if (!searchValue) {
      this.addressList = [];
      this.loading = false;
      return;
    }

    if (!navigator.onLine) {
      console.log('[QWA] 📡 Sem conexão — entrando em modo offline');
      this.enterOfflineMode();
      return;
    }

    console.log('[QWA] 🔎 Buscando:', searchValue);

    try {
      const predictions = await this.googlePlacesService.searchPredictions(
        searchValue,
        this.question.configuration?.countryRestriction,
      );
      console.log('[QWA] ✅ Resultados recebidos:', predictions.length, predictions.map(p => p.description));
      this.zone.run(() => {
        this.addressList = predictions;
        this.apiError = null;
        this.loading = false;
        this.change();
      });
    } catch (err) {
      const errType = err instanceof GoogleApiError ? err.type : 'api_error';
      console.warn('[QWA] ❌ Erro na busca:', errType, err);
      this.zone.run(() => {
        this.loading = false;
        this.addressList = [];
        this.apiError = errType;
        this.change();
      });
    }
  }

  // -------------------------------------------------------------------------
  // Online mode — select address
  // -------------------------------------------------------------------------

  async selectAddress(
    addressSelected: google.maps.places.AutocompletePrediction,
  ): Promise<void> {
    console.log('[QWA] 📍 Endereço selecionado:', addressSelected.description, '| place_id:', addressSelected.place_id);
    try {
      const result = await this.googlePlacesService.getPlaceDetails(
        addressSelected.place_id,
      );
      console.log('[QWA] 🗺️  Detalhes do lugar recebidos', {
        formattedAddress: result.formattedAddress,
        latitude: result.additionalInfo.latitude,
        longitude: result.additionalInfo.longitude,
        resolvedVia: result.additionalInfo.resolvedVia,
        city: result.additionalInfo.city,
        country: result.additionalInfo.country,
        postalCode: result.additionalInfo.postalCode,
      });
      this.zone.run(() => {
        this.additional_info = result.additionalInfo;
        if (result.formattedAddress) {
          this.currentMask = null;
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.addressForm.controls.address.setValue(result.formattedAddress ?? '', { emitEvent: false });
            });
          });
        }
        // CPK-4327: Google Places sometimes returns a lot-range street_number (e.g.
        // "133-135", common in BH). Silently blanking the field left a required input
        // empty with no explanation, so the user had to guess a replacement value
        // (a real case ended up with "000" entered this way). Use the first number
        // in the range instead — it's the actual premise being validated.
        if (this.additional_info.streetNumber.includes('-')) {
          this.additional_info.streetNumber = this.additional_info.streetNumber.split('-')[0].trim();
        }
        this.addressForm.controls.addtional_information.setValue(this.additional_info);
        this.showAdd = true;
        this.validateRequiredFields();
        this.change();
      });
    } catch (err) {
      console.warn('[QWA] ⚠️  Falha ao buscar detalhes do lugar — modo manual:', err);
      this.zone.run(() => {
        // Detail fetch failed — show manual fields so user can complete
        this.additional_info = { ...EMPTY_PLACE_ADDITIONAL_INFO, resolvedVia: 'manual' };
        this.showAdd = true;
        this.validateRequiredFields();
        this.change();
      });
    }
  }

  // -------------------------------------------------------------------------
  // Mode switching
  // -------------------------------------------------------------------------

  enterOfflineMode(): void {
    console.log('[QWA] 🔀 Entrando em modo OFFLINE (manual)');
    this.mode = 'offline';
    this.addressList = [];
    this.apiError = null;
    this.loading = false;
    this.showAdd = true;
    this.additional_info.resolvedVia = 'manual';
    this.change();
  }

  retryOnlineMode(): void {
    console.log('[QWA] 🔀 Voltando ao modo ONLINE');
    this.mode = 'online';
    this.apiError = null;
    this.showAdd = false;
    this.addressForm.reset();
    this.change();
  }

  // -------------------------------------------------------------------------
  // Offline mode — CEP lookup
  // -------------------------------------------------------------------------

  onPostalCodeInput(value: string): void {
    if (this.mode !== 'offline') return;
    this.cepSubject.next(value ?? '');
  }

  private async performCepLookup(cep: string): Promise<void> {
    const clean = cep.replace(/\D/g, '');
    this.additional_info.latitude = null;
    this.additional_info.longitude = null;
    if (clean.length < 5) return;

    this.cepLoading = true;
    console.log('[QWA] 📮 CEP lookup iniciado:', clean);

    if (this.viaCepService.isValidBrazilianCep(clean)) {
      console.log('[QWA] 🇧🇷 CEP brasileiro — tentando ViaCEP');
      const viaCepResult = await this.viaCepService.searchByCep(clean);
      if (viaCepResult) {
        console.log('[QWA] ✅ ViaCEP respondeu:', viaCepResult);
        const mapped = this.viaCepService.mapToAddressFields(viaCepResult);
        this.applyAddressFields(mapped, 'viacep');
        console.log('[QWA] 🌐 Geocodificando CEP para lat/lng...');
        const coords = await this.geocodingService.geocodeByCep(clean, 'Brazil', {
          streetName: mapped.streetName,
          neighborhood: mapped.neighborhood,
          city: mapped.city,
          state: mapped.state,
        });
        if (coords) {
          console.log('[QWA] 📌 Lat/Lng via ViaCEP+Geocoding:', coords);
          this.additional_info.latitude = coords.lat;
          this.additional_info.longitude = coords.lng;
        } else {
          console.warn('[QWA] ⚠️  Geocoding não retornou coords para o CEP');
        }
      } else {
        console.warn('[QWA] ⚠️  ViaCEP falhou — fallback para geocodePostalCode');
        await this.geocodePostalCode(clean);
      }
    } else {
      console.log('[QWA] 🌍 CEP não-brasileiro — usando geocodePostalCode');
      await this.geocodePostalCode(clean);
    }

    console.log('[QWA] 📌 Estado final do lookup:', {
      resolvedVia: this.additional_info.resolvedVia,
      latitude: this.additional_info.latitude,
      longitude: this.additional_info.longitude,
      city: this.additional_info.city,
      country: this.additional_info.country,
    });

    this.cepLoading = false;
    this.validateRequiredFields();
    this.addressForm.controls.addtional_information.setValue(this.additional_info);
    this.change();
  }

  private async geocodePostalCode(postalCode: string): Promise<void> {
    const query = this.additional_info.country
      ? `${postalCode}, ${this.additional_info.country}`
      : postalCode;
    console.log('[QWA] 🌐 geocodePostalCode query:', query);
    const coords = await this.geocodingService.geocodeAddress(query);
    if (coords) {
      console.log('[QWA] 📌 Lat/Lng via Geocoding:', coords);
      this.additional_info.latitude = coords.lat;
      this.additional_info.longitude = coords.lng;
      this.additional_info.resolvedVia = 'geocoding';
    } else {
      console.warn('[QWA] ⚠️  Geocoding não retornou resultado — resolvedVia=manual');
      this.additional_info.resolvedVia = 'manual';
    }
  }

  private applyAddressFields(
    fields: Partial<PlaceAdditionalInfo>,
    resolvedVia: PlaceAdditionalInfo['resolvedVia'],
  ): void {
    for (const key of Object.keys(fields) as (keyof PlaceAdditionalInfo)[]) {
      const incoming = fields[key];
      if (incoming !== undefined && incoming !== null && incoming !== '') {
        (this.additional_info as any)[key] = incoming;
      }
    }
    this.additional_info.resolvedVia = resolvedVia;
  }

  // -------------------------------------------------------------------------
  // Validation & form sync
  // -------------------------------------------------------------------------

  validateRequiredFields(): void {
    const fieldValidation = this.question.configuration?.fieldValidation;

    for (const field of Object.keys(this.requiredFields)) {
      const value = (this.additional_info as any)[field];
      const hasValue = Boolean(value && value.toString().trim().length > 0);

      if (!hasValue) {
        this.fieldValidations[field] = false;
        this.fieldErrorKeys[field] = null;
        continue;
      }

      const errorKey = this.checkFieldFormat(field, value.toString(), fieldValidation);
      this.fieldValidations[field] = !errorKey;
      this.fieldErrorKeys[field] = errorKey;
    }

    // Optional fields have no presence requirement, but the GLOBAL deny-pattern
    // still applies to them once filled in.
    for (const field of this.optionalValidatedFields) {
      // `county` is hidden (*ngIf="showCounty") for countries that don't use
      // it; a stale value from a previously-selected country must not block
      // submission behind an invisible field's error.
      if (field === 'county' && !this.showCounty) {
        this.fieldValidations[field] = true;
        this.fieldErrorKeys[field] = null;
        continue;
      }

      const value = (this.additional_info as any)[field];
      const hasValue = Boolean(value && value.toString().trim().length > 0);

      if (!hasValue) {
        this.fieldValidations[field] = true;
        this.fieldErrorKeys[field] = null;
        continue;
      }

      const errorKey = this.checkFieldFormat(field, value.toString(), fieldValidation);
      this.fieldValidations[field] = !errorKey;
      this.fieldErrorKeys[field] = errorKey;
    }
  }

  areRequiredFieldsValid(): boolean {
    return [...Object.keys(this.requiredFields), ...this.optionalValidatedFields].every(
      (field) => this.fieldValidations[field],
    );
  }

  /**
   * CPK-4150: checks a single field's value against
   * `question.configuration.fieldValidation`, returning a translation key
   * for the specific failure, or `null` when the value is valid / no rule
   * is configured for it.
   */
  private checkFieldFormat(
    field: string,
    value: string,
    config: AddressFieldValidationConfig | undefined,
  ): string | null {
    if (!config) return null;

    const rule: AddressFieldValidationRule | undefined = (config as any)[field];
    const globalDenyPattern = config._global?.denyPattern;

    if (rule?.minLength && value.trim().length < rule.minLength) {
      return 'AddressFieldTooShort';
    }

    if (rule?.requireAlphanumeric && !/[a-zA-Z0-9]/.test(value)) {
      return 'AddressFieldMustBeAlphanumeric';
    }

    if (field === 'postalCode' && rule?.brazilExactDigits && this.isBrazilianAddress()) {
      const digits = value.replace(/\D/g, '');
      if (digits.length !== rule.brazilExactDigits) {
        return 'AddressPostalCodeInvalidDigits';
      }
    }

    const denyPattern = rule?.denyPattern ?? globalDenyPattern;
    if (denyPattern && new RegExp(denyPattern).test(value)) {
      return 'AddressFieldInvalidCharacters';
    }

    return null;
  }

  // CPK-4337: public — bound directly from the template to gate the CEP input mask.
  isBrazilianAddress(): boolean {
    const country = (this.additional_info.country ?? '').trim().toLowerCase();
    return BRAZIL_COUNTRY_NAMES.has(country);
  }

  onFieldChange(): void {
    this.validateRequiredFields();
    this.addressForm.controls.addtional_information.setValue(this.additional_info);

    // In offline mode the address input is hidden, so keep it in sync with a
    // human-readable composite so that answer.value is never null/empty.
    if (this.mode === 'offline') {
      const { streetNumber, streetName, city, state, country, postalCode } = this.additional_info;
      const parts = [streetNumber, streetName, city, state, postalCode, country].filter(Boolean);
      this.addressForm.controls.address.setValue(parts.join(', '), { emitEvent: false });
    }

    this.change();
  }

  // -------------------------------------------------------------------------
  // Emit answer
  // -------------------------------------------------------------------------

  change(): void {
    if (this.showAdd || this.mode === 'offline') {
      this.validateRequiredFields();
    }

    const isAddressValid = this.addressForm.valid;
    const areRequiredFieldsValid =
      this.showAdd || this.mode === 'offline'
        ? this.areRequiredFieldsValid()
        : true;

    this.question.isValid = isAddressValid && areRequiredFieldsValid;
    this.question.answer!.value = this.addressForm.controls.address.value;
    this.question.answer!.addtional_information =
      this.addressForm.controls.addtional_information.value;

    this.changeAnswer.next({
      answer: this.question.answer!,
      question: this.question,
    });
  }
}
